import React, { useState, useEffect } from 'react';
import { Box, Typography, FormControl, Select, MenuItem } from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import CustomDatePicker from '../common/CustomDatePicker';
import { useStoreDetails } from '../common/Providers/StoreDetailsProvider/UseStoreDetails';
import { useCustomerReservation } from '../common/Providers/CustomerReservationProvider/UseCustomerReservation';

import { useResponsiveValues } from '../common/Providers/DimentionsProvider/UseResponsiveValues';
interface props {
  sx?: object;
  contentStyle?: object;
}

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const ReservationTerm: React.FC<props> = ({sx, contentStyle}) => {

  const { storeDetails } = useStoreDetails();
  const { ReservationMain, setReservationValue } = useCustomerReservation();
  const { matches900 } = useResponsiveValues();
  const [minDate, setMinDate] = useState(dayjs().set('hour', 0).set('minute', 0).set('second', 0));

  const allowPartial = storeDetails.allow_partial_day_rentals;
  const isSameDay = !!ReservationMain.pickup && !!ReservationMain.dropoff && dayjs(ReservationMain.pickup).isSame(dayjs(ReservationMain.dropoff), 'day');
  const hourSlots = (ReservationMain.headerData || [])
    .filter((h:any) => h.duration_type === 1)
    .sort((a:any, b:any) => a.hours - b.hours);
  const allowSameDay = allowPartial && hourSlots.length > 0;
  const showHourSlots = allowSameDay && isSameDay;
  const isPartialRental = showHourSlots && /(\d+)-hour/.test(String(ReservationMain.selectedDuration || ''));

  const selectedSlotId = (() => {
    const match = String(ReservationMain.selectedDuration || '').match(/(\d+)-hour/);
    if (!match) return '';
    const hrs = Number(match[1]);
    return hourSlots.find((h:any) => h.hours === hrs)?.id ?? '';
  })();

  const handleHourSlotChange = (event:any) => {
    const slotId = event.target.value;
    const slot = hourSlots.find((h:any) => h.id === slotId);
    if (!slot || !ReservationMain.pickup) return;

    // dropoff used for pricing: pickup instant + selected hours so CalcPrice diff
    // equals slot.milliseconds exactly, regardless of local timezone.
    const dropoffForPricing = new Date(ReservationMain.pickup.getTime() + slot.hours * 60 * 60 * 1000);

    // Display/record times derived from the store pickup time (independent of date instant).
    const parsedStart = dayjs(storeDetails.pickup_time, ['h:mm A', 'hh:mm A', 'H:mm']);
    const startTime = parsedStart.isValid() ? parsedStart : dayjs().startOf('day');
    const endTime = startTime.add(slot.hours, 'hour');

    setReservationValue('selectedDuration', `${slot.hours}-hour`);
    setReservationValue('dropoff', dropoffForPricing);
    setReservationValue('pickup_time', startTime.format('h:mm A'));
    setReservationValue('dropoff_time', endTime.format('h:mm A'));
  };

  useEffect(()=>{
    const storeTimezone = "America/New_York";
    let minDate = dayjs(storeDetails.valid_start_date).tz(storeTimezone).startOf('day');
    setMinDate(minDate);
    
    // if(ReservationMain.pickup === null) setReservationValue('pickup', new Date(minDate.toString()));
    let nextAvailableDate = findNextAvailableDate(minDate, storeDetails.blockDates);
  
    setMinDate(nextAvailableDate);
    if(ReservationMain.pickup === null) setReservationValue('pickup', new Date(nextAvailableDate.toString()));
  }, [])

  const findNextAvailableDate = (startDate:any, blockDates:any) => {
    let currentDate = dayjs(startDate);
    
    // Convert blockDates to a set of date strings for faster lookup
    const blockedDatesSet = new Set(
      blockDates.map((blockDate:any) => {
        // Assuming blockDate.date is already in YYYY-MM-DD format or is a Date object
        if (blockDate.date instanceof Date) {
          return blockDate.date.toISOString().split('T')[0];
        }
        return blockDate.date;
      })
    );
    
    // Keep incrementing the date until we find one that's not blocked
    while (blockedDatesSet.has(currentDate.format('YYYY-MM-DD'))) {
      currentDate = currentDate.add(1, 'day');
    }
    
    return currentDate;
  };

  const handlePickupChange = (value: any) => {
    const pickupDateTime = new Date(value);
    pickupDateTime.setHours(0, 0, 0, 0);

    setReservationValue('pickup', pickupDateTime);
    if(ReservationMain.dropoff !== null){
      if(allowSameDay){
        // Same-day rentals allowed, so the end date just needs to be >= start date.
        if(pickupDateTime.getTime() > ReservationMain.dropoff.getTime()) {
          setReservationValue('dropoff', new Date(pickupDateTime.getTime()));
        }
      } else if(pickupDateTime.getTime() + (24 * 60 * 60 * 1000) >= ReservationMain.dropoff.getTime()) {
        const newDropoffDateTime = new Date(pickupDateTime.getTime() + (24 * 60 * 60 * 1000));
        setReservationValue('dropoff', newDropoffDateTime);
      }
    }
  }

  const handleDropoffChange = (value: any) => {
    const dropoffDateTime = new Date(value);
    dropoffDateTime.setHours(0, 0, 0, 0);
    setReservationValue('dropoff', dropoffDateTime)
    // Reset any previously selected partial-day duration when the end date changes
    if (ReservationMain.selectedDuration) {
      setReservationValue('selectedDuration', null);
      setReservationValue('pickup_time', null);
      setReservationValue('dropoff_time', null);
    }
  }

  return (
    <Box sx={sx}>
      <Typography sx={{textDecoration:'underline', fontSize:'20px', marginBottom:'6px'}}>{`Reservation Term`}</Typography>
      <Box sx={{ display: 'flex', ...contentStyle, flexDirection: matches900?'row':'column'}}>
        <CustomDatePicker
          name="Start Date"
          sx={{ boxSizing: 'boder-box', width: matches900?'250px':'100%', pr: '20px', mb:'20px' }}
          value={dayjs(ReservationMain.pickup)}
          onChange={handlePickupChange}
          // maxDate={dayjs(ReservationMain.dropoff)}
          shouldDisableDate={(date:any)=>{
            return storeDetails.blockDates.find((bDate:any)=>{
              const blDate = dayjs(bDate.date);
              if(date.isSame(blDate) || date.isAfter(blDate)) return Math.abs(date.diff(blDate)) <= (bDate.duration - 1) * 24 * 3600 * 1000;
              else 
              return false;
            });
          }}
          minDate = {minDate}
        />
        <CustomDatePicker
          name="End Date"
          sx={{ boxSizing: 'boder-box', width: matches900?'250px':'100%', pr: '20px', mb:'20px' }}
          value={dayjs(ReservationMain.dropoff)}
          onChange={handleDropoffChange}
          minDate={allowSameDay
            ? dayjs(ReservationMain.pickup ? dayjs(ReservationMain.pickup).startOf('day') : minDate.startOf('day'))
            : dayjs(ReservationMain.pickup ? dayjs(ReservationMain.pickup).add(1, 'day').set('hour', 0).set('minute', 0).set('second', 0) : minDate.add(1, 'day').set('hour', 0).set('minute', 0).set('second', 0))}
          shouldDisableDate={(date:any)=>{
            return storeDetails.blockDates.find((bDate:any)=>{
              const blDate = dayjs(bDate.date);
              if(date.isSame(blDate) || date.isAfter(blDate)) return Math.abs(date.diff(blDate)) <= (bDate.duration - 1) * 24 * 3600 * 1000;
              else 
              return false;
            });
          }}
        />
      </Box>
      {showHourSlots && (
        <FormControl sx={{ boxSizing: 'border-box', width: matches900?'540px':'100%', pr: matches900?'20px':0, mb:'20px' }}>
          <Typography style={{ fontSize: '15px' }}>{`Duration`}</Typography>
          <Select
            labelId="hour-slot-label"
            value={selectedSlotId}
            displayEmpty
            onChange={handleHourSlotChange}
            sx={{ mt: '2px', boxShadow: '2px 2px 6px #b3b3b3', borderRadius: '3px' }}
          >
            <MenuItem value="" disabled>{`Select duration`}</MenuItem>
            {hourSlots.map((slot:any) => (
              <MenuItem key={slot.id} value={slot.id}>{slot.header}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {isPartialRental ? (
        <Typography sx={{marginTop:"30px", fontSize:'18px'}}>
          {`Your rental starts at `}
          <b>{`${ReservationMain.pickup_time || storeDetails.pickup_time} `}{dayjs(ReservationMain.pickup).format('MM/DD/YYYY')}</b>
          {` and ends at `}
          <b>{`${ReservationMain.dropoff_time || storeDetails.dropoff_time} `}{dayjs(ReservationMain.dropoff).format('MM/DD/YYYY')}</b>
        </Typography>
      ) : showHourSlots ? (
        <Typography sx={{marginTop:"30px", fontSize:'18px'}}>
          {`Please select a duration to see your rental term.`}
        </Typography>
      ) : (
      <Typography sx={{marginTop:"30px", fontSize:'18px'}}>
        {`Your rental starts on when picked up on `}
        {ReservationMain.pickup ? <b>{`${storeDetails.pickup_time} `}{dayjs(ReservationMain.pickup).format('MM/DD/YYYY')}</b> : <b>{"n/a"}</b>}
        {` and ends at `}
        {ReservationMain.dropoff ? <b>{`${storeDetails.dropoff_time} `}{dayjs(ReservationMain.dropoff).format('MM/DD/YYYY')}</b> : <b>{"n/a"}</b>}
      </Typography>
      )}
    </Box>
  );
}

export default ReservationTerm;